import { useFetchWrapper } from "../hooks/useFetchWrapper";
import { useLoading } from "../utils/LoadingContext";
import {
    formatAPIErrorLog,
    formatAPIResponseLog,
    getFormattedCurrentTime,
    notifyError,
    notifySuccess
} from "../utils/helpers";
import {
    GEN_AI_INVALID_INST_ERROR,
    GEN_AI_INVALID_VIDEO_ERROR, GEN_AI_INVALID_VIDEO_FILE_TOO_LARGE_ERROR,
    GEN_AI_INVALID_VIDEO_LINK_DOMAIN_ERROR,
    GEN_AI_INVALID_VIDEO_LINK_ERROR,
    GENERATE_QUESTION_ERROR,
    LOAD_PLATFORM_SETTINGS_ERROR,
    LOAD_SSO_ERROR,
    SERVER_ERROR,
    SSO_UPDATE_SUCCESS,
    UPDATE_LMS_ERROR,
    UPDATE_LMS_SUCCESS,
    UPDATE_PLATFORM_SETTINGS_ERROR
} from "../utils/toast-message-constants";
import {useConfig} from "../hooks/useConfig";

export const InstitutionApiCalls = () => {
    const fetchWrapper = useFetchWrapper();
    const config = useConfig();
    const {setLoading} = useLoading();
    return {
        uploadImg,
        uploadLogoImg,
        saveSSODetails,
        getSSODetails,
        saveLMSConfig,
        getSetting,
        updateSetting,
        questionGenerate,
    }


    function uploadImg(file, refreshFunc) {
        const fileName = `${getFormattedCurrentTime()}${getSuffix(file.name)}`;

        const cleanedFileName = removeSpecialCharacters(fileName);
        return fetchWrapper.put({
            url: `${config.api.institution}/institution/bgImg`,
            body: {originalName: cleanedFileName}
        }).then(result => {
            if (result.success) {
                return result.result;
            } else {
                notifyError(result.errorMessage);
                return "";
            }
        }).then(({putURL: url, getURL}) => {
            if (!url) {
                return "";
            }
            const requestOptions = {
                headers: {"Content-Type": file.type},
                method: 'PUT',
                body: file
            };
            return fetch(url, requestOptions).then(res=> {
                if (refreshFunc) {
                    refreshFunc(getURL);
                }
            });
        }).catch(err => {
            console.log(err);
            notifyError(SERVER_ERROR);
        });
    }


    function uploadLogoImg(file, refreshFunc) {
        const fileName = `${getFormattedCurrentTime()}${getSuffix(file.name)}`;

        const cleanedFileName = removeSpecialCharacters(fileName);
        return fetchWrapper.put({
            url: `${config.api.institution}/institution/logoImg`,
            body: {originalName: cleanedFileName}
        }).then(result => {
            if (result.success) {
                return result.result;
            } else {
                notifyError(result.errorMessage);
                return "";
            }
        }).then(({putURL: url, getURL}) => {
            if (!url) {
                return "";
            }
            const requestOptions = {
                headers: {"Content-Type": file.type},
                method: 'PUT',
                body: file
            };
            return fetch(url, requestOptions).then(res=> {
                console.log(res);
                if (refreshFunc) {
                    refreshFunc(getURL);
                }
            });
        }).catch(err => {
            console.log(err);
            notifyError(SERVER_ERROR);
        });
    }

    function saveSSODetails(configJson, integrationType) {
        setLoading(true);
        return fetchWrapper.put(
            {
                url: `${config.api.institution}/institution/ssoconfig`,
                body: {
                    ssoConfigJsonString: JSON.stringify(configJson),
                    integrationType: integrationType
                }
            }
        ).then((result) => {
            console.log(result);
            if (result.success) {
                notifySuccess(SSO_UPDATE_SUCCESS);
            } else {
                notifyError(result.errorMessage);
            }
            return result;
        },
            (error) => {
                notifyError(SSO_UPDATE_SUCCESS);
                console.log(error);
            }).finally(() => {
                setLoading(false);
            });
    }

    function getSSODetails() {
        return fetchWrapper.get(
            {
                url: `${config.api.institution}/institution/ssoconfig`,
                authorizeRequired: true
            }
        )
        .then(res => {
            if (res.success) {
                return res.result;
            }
        })
        .catch(err => {
            console.log(err);
            notifyError(LOAD_SSO_ERROR);
        });

    }

    function saveLMSConfig(configJson, integrationType) {
        setLoading(true);
        return fetchWrapper.put(
            {
                url: `${config.api.institution}/institution/lmsconfig`,
                body: {
                    lmsConfigJsonString: JSON.stringify(configJson),
                    integrationType: integrationType
                }
            }
        ).then((result) => {
            console.log(result);
            if (result.success) {
                notifySuccess(UPDATE_LMS_SUCCESS);
            } else {
                notifyError(result.errorMessage);
            }
            return result;
        },
            (error) => {
                notifyError(UPDATE_LMS_ERROR);
                console.log(error);
            }).finally(() => {
                setLoading(false);
            });
    }

    function getSetting() {
        return fetchWrapper.get({
            url: `${config.api.institution}/institutions/platformsetting`,
            authorizeRequired: true
        }).then(res => {
            // console.log(res);
            if (res.success) {
                formatAPIResponseLog("getSetting", res.result);
                return res.result;
            }
        }).catch(err => {
            console.log(err);
            formatAPIErrorLog("getSetting", err);
            notifyError(LOAD_PLATFORM_SETTINGS_ERROR);
        });
    }

    function updateSetting(setting) {
        return fetchWrapper.put({
            url: `${config.api.institution}/institutions/platformsetting`,
            body: {
                setting: setting,
            }
        }).then((result) => {
            // console.log(result);
            if (!result.success) {
                notifyError(result.errorMessage);
            }
            return result;
        }).catch(error => {
            notifyError(UPDATE_PLATFORM_SETTINGS_ERROR);
            console.log(error);
        });
    }

    function questionGenerate(videoLink, questionType) {
        return fetchWrapper.get({
            url: `${config.api.institution}/institution/question/gen?videoLink=${videoLink}&questionType=${questionType}`,
        }).then((result) => {
            // console.log(result);
            if (result.success) {
                return result.result;
            } else {
                notifyError(result.errorMessage);
                return "";
            }
        }).catch(error => {
            let errorObj = JSON.parse(error.message);
            if (errorObj.errorCode === "Y023") {
                notifyError(GEN_AI_INVALID_INST_ERROR);
            } else if (errorObj.errorCode === "Y024") {
                notifyError(GEN_AI_INVALID_VIDEO_ERROR);
            } else if (errorObj.errorCode === "Y025") {
                notifyError(GEN_AI_INVALID_VIDEO_LINK_ERROR);
            } else if (errorObj.errorCode === "Y026") {
                notifyError(GEN_AI_INVALID_VIDEO_LINK_DOMAIN_ERROR);
            } else if (errorObj.errorCode === "Y027") {
                notifyError(GEN_AI_INVALID_VIDEO_FILE_TOO_LARGE_ERROR);
            } else {
                notifyError(GENERATE_QUESTION_ERROR);
            }
            console.log(error);
            return "";
        });
    }

    function removeSpecialCharacters(fileName) {
        const regex = /[^a-zA-Z0-9.]+/g;
        return fileName.replace(regex, '');
    }

    function getSuffix(imageName) {
        const regexp = /(.+)(\.\w+)/g;
        return regexp.exec(imageName)[2];
    }
}